/**
 * Notification Status Banner
 * 
 * Shows a dismissible banner encouraging users to enable notifications
 * Only shows if notifications are not enabled
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from '../lib/motion-utils';
import { Bell, X } from 'lucide-react';
import { Button } from './ui/button';
import { areNotificationsEnabled } from '../lib/notification-service';

export function NotificationStatusBanner() {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already enabled
    if (areNotificationsEnabled()) {
      return;
    }

    // Check if user has dismissed
    const wasDismissed = localStorage.getItem('tension_notification_banner_dismissed');
    if (wasDismissed) {
      return;
    }

    // Check if user has at least 2 check-ins (so they understand the value)
    const checkins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    if (checkins.length >= 2) {
      setShow(true);
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('tension_notification_banner_dismissed', 'true');
    setTimeout(() => setShow(false), 300);
  };

  const handleEnable = () => {
    navigate('/notification-settings');
  };

  return (
    <AnimatePresence>
      {show && !dismissed && (
        <motion.div
          key="notification-banner"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-6"
        >
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 relative">
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1 hover:bg-white/50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-blue-600" />
            </button>

            <div className="flex items-start gap-3 pr-8">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bell className="w-5 h-5 text-blue-600" />
              </div>
              
              <div className="flex-1 space-y-2">
                <div>
                  <h3 className="font-medium text-blue-900">Enable Daily Reminders</h3>
                  <p className="text-sm text-blue-800 mt-1">
                    Stay consistent with gentle notifications. Users with reminders are <strong>3x more likely</strong> to maintain their check-in streak.
                  </p>
                </div>
                
                <Button
                  onClick={handleEnable}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                >
                  Set Up Notifications
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}